import { Link } from 'react-router-dom'
import { Card } from './Card.jsx'

/**
 * Render a recoverable cross-facility 403 with a one-tap `tel:` call
 * button — turns a "you can't act on this" error into an actionable
 * coordination point.
 *
 * Triggered when the backend's exception envelope carries one of the
 * cross-facility AppError codes:
 *
 *   - cross_pharmacy_access      (PrescriptionViewSet, PrescriptionItem)
 *   - cross_laboratory_access    (LabOrder, LabOrderItem, Analyte)
 *   - cross_centre_access        (ImagingOrder)
 *   - cross_service_access       (AmbulanceRequest)
 *
 * Each of those raises an AppError with details:
 *
 *   {
 *     "routed_to": { "id", "name", "phone" },
 *     "staffed_at": { "id", "name" }
 *   }
 *
 * Returns null when the error doesn't match (caller falls back to the
 * generic ErrorBanner). When it matches, renders a Card with the
 * envelope's human message + a red Call button that uses `tel:` so
 * the operator can phone-coordinate the handover in one tap.
 *
 * Props:
 *   error    — the thrown error/axios error from the React-Query call
 *   backTo   — route to navigate back to (e.g. "/pharmacy")
 *   backLabel — link text, default "Back"
 */
export default function CrossFacilityErrorCard({ error, backTo, backLabel = 'Back' }) {
  const env = error?.response?.data?.error
  const CROSS_CODES = new Set([
    'cross_pharmacy_access',
    'cross_laboratory_access',
    'cross_centre_access',
    'cross_service_access',
  ])
  if (!env || !CROSS_CODES.has(env.code)) return null
  const routed = env?.details?.routed_to || {}
  const phone = (routed.phone || '').trim()
  const telHref = phone ? `tel:${phone.replace(/\s+/g, '')}` : null
  return (
    <Card>
      <div className="text-[11px] uppercase tracking-wide text-amber-700">
        Cross-facility request
      </div>
      <div className="mt-1 text-sm text-slate-800">{env.message}</div>
      {phone && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a
            href={telHref}
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white rounded px-3 py-2 text-sm"
          >
            ☎ Call {routed.name || 'facility'} · {phone}
          </a>
          <span className="text-xs text-slate-500">
            Faster than re-routing for an in-flight handover.
          </span>
        </div>
      )}
      {backTo && (
        <div className="mt-3">
          <Link to={backTo} className="text-xs underline">{backLabel}</Link>
        </div>
      )}
    </Card>
  )
}
